import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
    createChatSocket,
    getCurrentCompanyId,
    logSocketEvent,
    registerCompanyScope
} from '../Services/socket/chatSocket';

const SocketContext = createContext({
    socket: null,
    unreadCount: 0
});

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const companyId = getCurrentCompanyId();
    const previousUnreadCountRef = useRef(0);
    const audioContextRef = useRef(null);

    const playUnreadNotification = () => {
        if (typeof window === 'undefined') return;

        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;

        const audioContext = audioContextRef.current || new AudioContextClass();
        audioContextRef.current = audioContext;

        if (audioContext.state !== 'running') {
            return;
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        const now = audioContext.currentTime;

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(880, now);
        gainNode.gain.setValueAtTime(0.0001, now);
        gainNode.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.start(now);
        oscillator.stop(now + 0.22);
    };

    useEffect(() => {
        if (!companyId) {
            setSocket(null);
            setUnreadCount(0);
            previousUnreadCountRef.current = 0;
            return undefined;
        }

        const nextSocket = createChatSocket(companyId);

        const handleConnect = () => {
            logSocketEvent('global connected', nextSocket.id);
            registerCompanyScope(nextSocket, companyId);
        };

        const handleDisconnect = (reason) => {
            logSocketEvent('global disconnected', reason);
        };

        const handleUnreadCountUpdated = (payload) => {
            logSocketEvent('unreadCountUpdated', payload);

            const nextUnreadCount = Number(payload?.unreadCount);
            setUnreadCount(Number.isFinite(nextUnreadCount) && nextUnreadCount > 0 ? nextUnreadCount : 0);
        };

        const handleSocketError = (error) => {
            logSocketEvent('global socketError', error);
            console.error('Socket error:', error);
        };

        nextSocket.on('connect', handleConnect);
        nextSocket.on('disconnect', handleDisconnect);
        nextSocket.on('unreadCountUpdated', handleUnreadCountUpdated);
        nextSocket.on('socketError', handleSocketError);

        if (nextSocket.connected) {
            handleConnect();
        }

        setSocket(nextSocket);

        return () => {
            nextSocket.off('connect', handleConnect);
            nextSocket.off('disconnect', handleDisconnect);
            nextSocket.off('unreadCountUpdated', handleUnreadCountUpdated);
            nextSocket.off('socketError', handleSocketError);
            nextSocket.disconnect();
        };
    }, [companyId]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const unlockAudio = () => {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) return;

            const audioContext = audioContextRef.current || new AudioContextClass();
            audioContextRef.current = audioContext;

            if (audioContext.state === 'suspended') {
                audioContext.resume().catch(() => { });
            }
        };

        window.addEventListener('pointerdown', unlockAudio, { passive: true });

        return () => {
            window.removeEventListener('pointerdown', unlockAudio);
        };
    }, []);

    useEffect(() => {
        if (unreadCount > previousUnreadCountRef.current) {
            playUnreadNotification();
        }

        previousUnreadCountRef.current = unreadCount;
    }, [unreadCount]);

    return (
        <SocketContext.Provider
            value={{
                socket,
                unreadCount
            }}
        >
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = () => useContext(SocketContext);
